import { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  extractErrorMessage,
  useGetRrcEp9WasteProfilesByEp9WasteQuery,
  useGetCurrentRrcEp9WasteProfilesByEp9WasteQuery,
} from '@ots-frontend/ots-data';
import { AdminPage, type AdminPageProps } from '../../components/AdminPage';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsEntityIdentitySelect } from '../../select/OtsEntityIdentitySelect';
import { OtsThemeRoot } from '../../theme/OtsTheme';
import { useApp, useAppDensity } from '../../app/AppContext';
import { useAuth } from '../../app/AuthProvider';
import { RrcEp9WasteProfilesView } from './RrcEp9WasteProfilesView';
import { RrcEp9WasteProfileInspector } from './RrcEp9WasteProfileInspector';

export type RrcEp9WasteProfilesPageProps = {
  objectId?: string;
  objectType?: string;
  objectName?: string;
  compact?: boolean;
  embedded?: boolean;
  readOnly?: boolean;
  layout?: AdminPageProps['layout'];
};
const Ep9WasteProfiles = ({
  objectId: fixedId,
  objectType: fixedType,
  objectName: fixedName,
  compact: compactProp,
  embedded,
  layout = 'document',
  readOnly = false,
}: RrcEp9WasteProfilesPageProps) => {
  const auth = useAuth(),
    app = useApp(),
    density = useAppDensity();
  const compact = compactProp ?? density === 'compact';
  const fill = layout === 'fill';
  const [location, setLocation] = useState({
    id: fixedId,
    type: fixedType ?? 'COMPANY',
    name: fixedName,
  });
  const [includeHistory, setIncludeHistory] = useState(false);
  const [selectedId, select] = useState<string>();
  const [creating, setCreating] = useState(false);
  const canView = auth.hasAccess('rrcep9wasteprofile_view');
  const canCreate = !readOnly && auth.hasAccess('rrcep9wasteprofile_create');
  const validType = ['COMPANY', 'SITE', 'WELL'].includes(location.type);
  const currentQuery = useGetCurrentRrcEp9WasteProfilesByEp9WasteQuery(
    canView && location.id && validType && !includeHistory
      ? { objectId: location.id, objectType: location.type }
      : skipToken
  );
  const historyQuery = useGetRrcEp9WasteProfilesByEp9WasteQuery(
    canView && location.id && validType && includeHistory
      ? { objectId: location.id, objectType: location.type }
      : skipToken
  );
  const query = includeHistory ? historyQuery : currentQuery;
  const content = !canView ? (
    <OtsInlineAlert title="Access denied" tone="warning">
      You do not have permission to view EP-9 waste profiles.
    </OtsInlineAlert>
  ) : (
    <>
      {!fixedId && (
        <div className={fill ? 'shrink-0 max-w-2xl' : 'mb-4 max-w-2xl'}>
          <OtsEntityIdentitySelect
            entityType={location.type}
            label="Subject"
            value={location.id}
            entityName={location.name}
            regionId={app?.regionId}
            size={compact ? 'compact' : 'default'}
            onChange={(id, type) => {
              setLocation({ id, type, name: undefined });
              select(undefined);
              setCreating(false);
            }}
            onNameChange={(name) =>
              setLocation((value) => ({ ...value, name }))
            }
          />
        </div>
      )}
      {!location.id || !validType ? (
        <OtsInlineAlert title="Select a subject">
          Choose a company, site, or well to manage its RRC registrations and
          profile history.
        </OtsInlineAlert>
      ) : (
        <RrcEp9WasteProfilesView
          fill={fill}
          profiles={(query.currentData ?? []).filter((profile) =>
            auth.hasAccess(profile, 'rrcep9wasteprofile_view')
          )}
          loading={query.isFetching}
          error={
            query.error
              ? extractErrorMessage(
                  query.error,
                  'EP-9 waste profiles could not be loaded.'
                )
              : undefined
          }
          compact={compact}
          includeHistory={includeHistory}
          onIncludeHistory={setIncludeHistory}
          onRefresh={() => void query.refetch()}
          onOpen={(profile) => select(profile.id)}
          onNew={canCreate ? () => setCreating(true) : undefined}
        />
      )}
      {selectedId && (
        <RrcEp9WasteProfileInspector
          id={selectedId}
          compact={compact}
          readOnly={readOnly}
          onClose={() => select(undefined)}
          onChanged={() => void query.refetch()}
        />
      )}
      {creating && !readOnly && (
        <RrcEp9WasteProfileInspector
          initialMode="create"
          objectLocked
          compact={compact}
          defaults={{
            objectId: location.id,
            objectType: location.type,
            objectName: location.name,
          }}
          onClose={() => setCreating(false)}
          onChanged={() => void query.refetch()}
        />
      )}
    </>
  );
  const body = (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col gap-4' : 'contents'}>
      {content}
    </div>
  );
  return (
    <OtsThemeRoot
      className={fill ? 'flex h-full min-h-0 flex-1 flex-col' : undefined}
    >
      {embedded ? (
        body
      ) : (
        <AdminPage
          title="Waste compliance profiles"
          eyebrow="Texas RRC"
          fullWidth
          layout={layout}
          density={compact ? 'compact' : 'default'}
          subtitle="Waste characterization, generator snapshots, and effective version history."
        >
          <div
            className={
              fill ? 'mt-4 flex min-h-0 flex-1 flex-col overflow-auto' : 'mt-5'
            }
          >
            {body}
          </div>
        </AdminPage>
      )}
    </OtsThemeRoot>
  );
};
export const RrcEp9WasteProfilesPage = (
  props: RrcEp9WasteProfilesPageProps
) => (
  <Ep9WasteProfiles
    key={JSON.stringify([props.objectType, props.objectId])}
    {...props}
  />
);
